import { ApplicationRef, Component, ElementRef, EventEmitter, forwardRef, Inject, Input, OnDestroy, OnInit, Optional, Output, ViewChild } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { InterviewService, MonitoringService } from '@App';
import { ActionContext } from 'app/action-context';
import { InterviewDTO_extended } from '@DTOs';
import { InterviewManagerService } from '@Interview';
import { InterviewDataService } from '@Services';
import { SubSink } from 'subsink';

import { InterviewSectionDialogAction, InterviewSectionDialogData, InterviewSectionDialogResponse } from './models';
import { SharedModule } from '@Shared/shared.module';
import { DynamicFormComponent } from '@Components/form-fields/dynamic-form/dynamic-form.component';
import { InterviewProcessingStatusComponent } from '../interview-processing-status/interview-processing-status.component';
import { InterviewNewFieldAlertComponent } from '../interview-new-field-alert/interview-new-field-alert.component';


@Component({
    selector: 'interview-section-dialog',
    templateUrl: './interview-section-dialog.component.html',
    styleUrls: ['./interview-section-dialog.component.scss'],
    imports: [SharedModule, forwardRef(() => DynamicFormComponent), InterviewProcessingStatusComponent, InterviewNewFieldAlertComponent]
})
export default class InterviewSectionDialog implements OnInit, OnDestroy {
    @ViewChild('sectionContainer') sectionContainer: ElementRef;

    @Input() sectionId: string;
    @Input() title: string;
    @Input() client: any;
    @Input() account: any;
    @Input() case: any;
    @Input() saveButtonLabel: string = 'Save Decision';
    // Continues the action that opened this section so its load calls are attributed to it.
    @Input() actionId: string;
    // When set, saving runs as an action with this name.
    @Input() saveActionName: string;
    @Output() onSectionSaved = new EventEmitter<void>();
    @Output() onDialogClosed = new EventEmitter<void>();

    dialogTitle = 'Section Requirement';
    interviewLoaded = false;
    sectionContainerHtmlId = 'interview-section-content';
    isModal = true;

    subs = new SubSink();

    constructor(                
        protected interviewService: InterviewService,
        private interviewDataService: InterviewDataService,
        private monitoringService: MonitoringService,
        protected interviewManager: InterviewManagerService,
        private appRef: ApplicationRef,
        @Optional() public dialogRef?: MatDialogRef<InterviewSectionDialog, InterviewSectionDialogResponse>,
        @Optional() @Inject(MAT_DIALOG_DATA) public data?: InterviewSectionDialogData,
    ) {
        if ( this.data) {
            this.interviewService.setAccount(this.data.account);
            this.saveButtonLabel = data.saveButtonLabel;
        }else{
            this.isModal = false;
        }
    }


    //#region Subscriptions

    subscribeInterviewData() {
        this.subs.add(this.interviewDataService.reloadInterviewForSection(this.data.case.id, this.data.sectionId).subscribe(interview => {
            // override active section with the specific section we want to show
            const interviewWithActiveSection: InterviewDTO_extended = { 
                ...interview, 
                activeSectionId: this.data.sectionId,
                sections: (interview.sections ?? []).map(section => ({
                    ...section,
                    questions: section.questions ?? []
                }))
            };
            this.interviewService.setInterviewData(interviewWithActiveSection);
            this.interviewManager.formatSectionData(interviewWithActiveSection);
            this.interviewLoaded = true;


        }));
    }

    subscribeToSavedInterviewResponse() {
        this.subs.add(this.interviewService.updatedInterview.subscribe(interview => {
            const interviewWithActiveSection: InterviewDTO_extended = { 
                ...interview, 
                activeSectionId: this.data.sectionId,
                sections: (interview.sections ?? []).map(section => ({
                    ...section,
                    questions: section.questions ?? []
                }))
            };
            this.interviewService.setInterviewData(interviewWithActiveSection);
            this.interviewManager.formatSectionData(interviewWithActiveSection);
            this.interviewLoaded = true;

            const anyInterviewErrors = this.interviewService.checkForSectionErrors(this.data.sectionId, interviewWithActiveSection.sections);
            if ((!anyInterviewErrors) || (interviewWithActiveSection.workflowCompleted)) {
                if (this.dialogRef) {
                this.dialogRef.close({
                    action: InterviewSectionDialogAction.Saved
                });
                }else{
                    this.onSectionSaved.emit();
                }
            }
        }));
    }

    subscribeToSavedInterviewError() {
        this.subs.add(this.interviewService.notifyUpdateInterviewError.subscribe(_ => {
            this.interviewLoaded = true;
        }));
    }
    //#endregion
    //#region Lifecycle

    async ngOnInit() {
        if (!this.data){
            this.data = {
                account: this.account,
                case: this.case,
                client: this.client,
                sectionId: this.sectionId,
                title: this.title,
                saveButtonLabel: this.saveButtonLabel,
                actionId: this.actionId,
                saveActionName: this.saveActionName
            };

            this.interviewService.setAccount(this.data.account);
        }

        this.dialogTitle = this.data.title;

        // Only the calls this dialog makes directly belong to the action; SignalR and subscription handlers do not.
        await ActionContext.resume(this.data.actionId, async () => {
            await this.interviewManager.init(this.data.client, this.data.account, this.data.case, this.sectionContainerHtmlId);
            await this.interviewService.loadAccountRules(this.data.account.id);
        });

        this.interviewManager.setupSignalRConnection();

        // The section load is triggered by this subscription, so it belongs to the action.
        ActionContext.resume(this.data.actionId, () => this.subscribeInterviewData());

        this.subscribeToSavedInterviewResponse();
        this.subscribeToSavedInterviewError();
    }

    ngOnDestroy() {
        this.subs.unsubscribe();
    }

    //#endregion
    //#region Handlers

    handleClose() {
        if (this.dialogRef) {
            this.dialogRef.close({
                action: InterviewSectionDialogAction.Canceled
            });
        }else{
            this.onDialogClosed.emit();
        }
    }

    async handleSave() {
        if (this.data.saveActionName) {
            await this.monitoringService.runAction(this.data.saveActionName, this.getActionProperties(), () => this.save());
        } else {
            await this.save();
        }
    }

    private getActionProperties(): { [key: string]: string } {
        const { client, account, case: caseDetail } = this.data;
        const properties = {
            clientCode: client?.code,
            accountCode: account?.code,
            accountId: account?.id,
            caseId: caseDetail?.id,
            caseNumber: caseDetail?.caseNumberDisplay ?? caseDetail?.caseNumber
        };
        return properties;
    }

    private async save() {
        // Let in-flight answer processing finish (and render any field it reveals) while the form is still shown;
        // hiding it first would tear down those controls before they are validated.
        if (await this.interviewService.waitForActionsIdle()) {
            this.appRef.tick();
        }
        this.interviewLoaded = false;
        const didInterviewManagerDelegateToInterviewService = await this.interviewManager.saveInterview();

        if (didInterviewManagerDelegateToInterviewService === false) {
            this.interviewLoaded = true;
        }
        // the dialog close will only happen when no errors are on the interview
        // See subscribeToSavedInterviewResponse function -> that code gets run when the interview.service.ts
        // updates an observable with the interview data after the save.
    }
    //#endregion
}
